import { Award, Shield, FileText } from "lucide-react";
import { Card, CardContent, Badge } from "@b2b/ui-components";

interface CertificationsSectionProps {
  certifications?: any[];
  businessInfo?: any;
}

export const CertificationsSection = ({
  certifications = [],
  businessInfo,
}: CertificationsSectionProps) => {
  if (certifications == null) {
    certifications = [];
  }
  const allCertifications = [...certifications];

  if (businessInfo?.qualityCertifications && businessInfo.qualityCertifications.length > 0) {
    businessInfo.qualityCertifications.forEach((cert: string) => {
      if (!allCertifications.find((c: any) => c.name === cert || (typeof c === "string" && c === cert))) {
        allCertifications.push({ name: cert });
      }
    });
  }

  return (
    <Card className="overflow-hidden border border-gray-100 bg-gradient-to-b from-gray-50/80 to-white shadow-sm">
      <CardContent className="p-4 sm:p-5 md:p-6">
        <header className="mb-4 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-6 w-6 text-[#FF6A00]" aria-hidden />
            <h2 className="text-2xl font-bold text-[#1A2A3D]">
              Certifications &amp; Quality
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            Quality certificates and standards this supplier has shared on their storefront.
          </p>
        </header>

        {allCertifications.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white/80 py-16 sm:py-20 text-center">
            <Shield className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No certifications listed yet</h4>
            <p className="text-sm text-gray-500 max-w-md mx-auto px-4">
              This supplier has not published certificates on their storefront.
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8 list-none m-0 p-0">
            {allCertifications.map((cert: any, index: number) => {
              const certName = typeof cert === "string" ? cert : cert.name || "Certification";
              const docUrl =
                typeof cert === "object" && cert
                  ? String(cert.imageUrl || cert.fileUrl || "").trim() || null
                  : null;
              const pathForType = (docUrl || "").split("?")[0] || "";
              const isPdf = /\.pdf(\?|$)/i.test(pathForType);
              const certImage = docUrl && !isPdf ? docUrl : null;
              const issuingAuthority = typeof cert === "object" ? cert.issuingAuthority : null;
              const verificationStatus = typeof cert === "object" ? cert.verificationStatus : null;

              return (
                <li key={index}>
                  <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-[#FF6A00]/35 hover:shadow-md">
                    {/* Large preview — dominant visual (readable at a glance) */}
                    <div className="relative flex min-h-[220px] w-full items-center justify-center bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 px-3 py-6 sm:min-h-[280px] sm:px-5 sm:py-10 md:min-h-[300px]">
                      {certImage ? (
                        <a
                          href={certImage}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full transition-opacity hover:opacity-90"
                          aria-label={`Open ${certName} in new tab`}
                        >
                          <img
                            src={certImage}
                            alt={certName}
                            className="max-h-[240px] w-full max-w-full object-contain sm:max-h-[280px] md:max-h-[300px]"
                          />
                        </a>
                      ) : isPdf && docUrl ? (
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-h-[200px] w-full flex-col items-center justify-center gap-3 p-6 text-center transition-colors hover:bg-red-50/50 sm:min-h-[240px]"
                          aria-label={`Open ${certName} PDF`}
                        >
                          <span className="flex h-20 w-20 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-100">
                            <FileText className="h-10 w-10" strokeWidth={1.5} aria-hidden />
                          </span>
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            PDF document — click to open
                          </span>
                        </a>
                      ) : (
                        <div className="flex min-h-[200px] w-full flex-col items-center justify-center gap-3 p-6 text-center sm:min-h-[240px]">
                          <Award
                            className="h-16 w-16 text-[#FF6A00]/40 sm:h-20 sm:w-20"
                            strokeWidth={1.25}
                            aria-hidden
                          />
                          <span className="text-xs text-gray-500">No preview image</span>
                        </div>
                      )}
                      {verificationStatus === "verified" && (
                        <span className="absolute right-3 top-3">
                          <Badge className="border-0 bg-emerald-600 text-white shadow-sm text-xs font-medium">
                            Verified
                          </Badge>
                        </span>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6 text-sm">
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold text-gray-900 leading-snug line-clamp-2 text-center">
                          {certName}
                        </h3>
                        {issuingAuthority && (
                          <p className="text-sm text-gray-500 line-clamp-2">{issuingAuthority}</p>
                        )}
                        {verificationStatus && verificationStatus !== "verified" && (
                          <Badge
                            className={`mt-1 w-fit text-xs ${
                              verificationStatus === "verified"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {verificationStatus}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
